import React, { useMemo } from 'react';
import { SxProps, Tooltip } from '@mui/material';
import { CaptionBox, CaptionTextField, ImageCaption } from './style';
import { Attachment } from '../AttachmentDisplay/type';
import { FileNameTypes } from '@/__generated__/graphql';
import { theme } from '@/theme/theme';
import { CAPTION_DISPLAY_TRUNCATE, CAPTION_MAX_LENGTH } from '@/constants';

type AttachmentCaptionBoxProps = {
  isUploadDialog?: boolean;
  isEditing: boolean;
  collectionName: string;
  attachmentCaption: string | undefined;
  setAttachmentCaption: (caption: string) => void;
  handleAttachmentCaption: () => void;
  setIsEditing: (editing: boolean) => void;
  handleCaption: (e: React.MouseEvent) => void;
  selectedAttachment: Attachment;
  isViewPoint?: boolean;
  sx?: SxProps;
  handleViewIn3D?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  location?: string;
  fluid?: boolean;
};

export const AttachmentCaption = ({
  isUploadDialog,
  isEditing,
  collectionName,
  attachmentCaption,
  setAttachmentCaption,
  handleAttachmentCaption,
  setIsEditing,
  handleCaption,
  selectedAttachment,
  isViewPoint,
  sx,
  handleViewIn3D,
  location = '',
  fluid = false,
}: AttachmentCaptionBoxProps) => {
  const attachmentName = useMemo(() => {
    const caption =
      attachmentCaption ||
      selectedAttachment.caption ||
      selectedAttachment.fileName ||
      selectedAttachment.name;

    return selectedAttachment.name === FileNameTypes.ReferenceDrawing ||
      selectedAttachment.name === FileNameTypes.IsometricDrawing
      ? `${caption} (Reference Drawing)`
      : caption;
  }, [attachmentCaption, selectedAttachment]);

  const displayAttachmentName = useMemo(() => {
    const name = attachmentName ?? '';
    return name.length > CAPTION_DISPLAY_TRUNCATE
      ? `${name.slice(0, CAPTION_DISPLAY_TRUNCATE).trimEnd()}...`
      : name;
  }, [attachmentName]);

  return (
    <CaptionBox onClick={handleCaption}>
      {!isUploadDialog && isEditing && collectionName !== 'paintRegion' ? (
        <CaptionTextField
          fluid={fluid}
          className={`captionTextField ${location}-attachment-caption-edit`}
          sx={sx}
          value={attachmentCaption ?? attachmentName}
          size="small"
          onChange={(e) => setAttachmentCaption(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') handleAttachmentCaption();
          }}
          onBlur={() => setIsEditing(false)}
          autoFocus
          multiline
          variant="outlined"
          slotProps={{
            input: {
              inputProps: { maxLength: CAPTION_MAX_LENGTH },
            },
          }}
          error={(attachmentCaption?.length ?? 0) >= CAPTION_MAX_LENGTH}
          helperText={
            (attachmentCaption?.length ?? 0) >= CAPTION_MAX_LENGTH
              ? `Maximum ${CAPTION_MAX_LENGTH} characters reached`
              : `${attachmentCaption?.length ?? 0}/${CAPTION_MAX_LENGTH}`
          }
        />
      ) : (
        <Tooltip
          arrow
          title={isViewPoint ? 'Ctrl+Click to open in new tab' : attachmentName}
          placement="top"
        >
          <ImageCaption
            fluid={fluid}
            onClick={(event: React.MouseEvent<HTMLButtonElement>) => {
              isViewPoint ? handleViewIn3D?.(event) : setIsEditing(true);
            }}
            style={
              isViewPoint
                ? {
                    color: theme.palette.common.darkBlue,
                    textDecoration: 'underline',
                    cursor: 'pointer',
                  }
                : {}
            }
          >
            {displayAttachmentName}
          </ImageCaption>
        </Tooltip>
      )}
    </CaptionBox>
  );
};
